import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';

export type DsInlineAlertType = 'neutral' | 'error' | 'warning' | 'success' | 'info';
export type DsInlineAlertActionLayout = 'stacked' | 'inline';
export type DsInlineAlertSize = 's' | 'm';
export type DsInlineAlertEmphasis = 'filled' | 'outline';
export type DsInlineAlertShadow = 'none' | 'l1' | 'l2' | 'l3';

@Component({
  selector: 'ds-inline-alert',
  standalone: true,
  imports: [CommonModule, DsIconButtonV2Component, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="ds-inline-alert" [class]="hostClasses" role="status">
      <span class="ds-inline-alert__icon" aria-hidden="true">
        <ds-icon [name]="iconName" size="var(--_icon-size)" [fill]="true"></ds-icon>
      </span>

      <div class="ds-inline-alert__layout">
        <div class="ds-inline-alert__content">
          @if (hasTitle) {
            <strong
              class="ds-inline-alert__title"
              [class.ds-text-h3-s-bold]="size === 'm'"
              [class.ds-text-h3-xs-bold]="size === 's'"
            >{{ title }}</strong>
          }
          <div
            class="ds-inline-alert__description"
            [class.ds-text-body-regular]="size === 'm'"
puedes            [class.ds-text-body-s-regular]="size === 's'"
          >
            <ng-content>{{ description }}</ng-content>
          </div>
        </div>

        <div class="ds-inline-alert__action">
          <ng-content select="[ds-inline-alert-action]" />
        </div>
      </div>

      @if (showCloseButton) {
        <ds-icon-button-v2
          class="ds-inline-alert__close"
          icon="Close"
          [size]="closeButtonSize"
          emphasis="terciary"
          [ariaLabel]="closeAriaLabel"
          (clicked)="dismissed.emit()"
        />
      }
    </section>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .ds-inline-alert {
      display: flex;
      align-items: flex-start;
      gap: var(--ds-inline-alert-icon-gap);
      width: 100%;
      box-sizing: border-box;
      padding: var(--_padding-y) var(--_padding-x);
      border: var(--_border-width) solid var(--_border-color);
      border-radius: var(--_border-radius);
      background: var(--_background);
      color: var(--ds-inline-alert-description-color);
    }

    .ds-inline-alert--shadow-none {
      box-shadow: none;
    }

    .ds-inline-alert--shadow-l1 {
      box-shadow: var(--ds-inline-alert-shadow-l1);
    }

    .ds-inline-alert--shadow-l2 {
      box-shadow: var(--ds-inline-alert-shadow-l2);
    }

    .ds-inline-alert--shadow-l3 {
      box-shadow: var(--ds-inline-alert-shadow-l3);
    }

    .ds-inline-alert__icon {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      color: var(--_semantic-color);
    }

    .ds-inline-alert__close {
      --ds-icon-button-terciary-foreground: var(--ds-inline-alert-close-icon-color);
    }

    .ds-inline-alert__layout {
      display: flex;
      min-height: var(--_icon-size);
      min-width: 0;
      flex: 1 1 0;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: var(--ds-inline-alert-action-gap);
    }

    .ds-inline-alert__content {
      display: flex;
      width: 100%;
      min-width: 0;
      flex: 1 1 auto;
      flex-direction: column;
      justify-content: center;
      gap: var(--ds-inline-alert-text-gap);
      color: var(--ds-inline-alert-description-color);
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    .ds-inline-alert--action-inline .ds-inline-alert__layout {
      flex-flow: row wrap;
      align-items: center;
      align-content: center;
      justify-content: flex-start;
    }

    .ds-inline-alert--action-inline .ds-inline-alert__content {
      width: auto;
      min-width: min(100%, var(--_action-content-min-width));
      flex: 1 1 var(--_action-content-min-width);
    }

    .ds-inline-alert__action {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
    }

    .ds-inline-alert__action:empty {
      display: none;
    }

    .ds-inline-alert__title {
      color: var(--ds-inline-alert-title-color);
    }

    .ds-inline-alert__description {
      display: flex;
      flex-direction: column;
      gap: var(--ds-inline-alert-text-gap);
      color: var(--ds-inline-alert-description-color);
    }

    :host ::ng-deep .ds-inline-alert__description > * {
      margin: 0;
    }

    :host ::ng-deep .ds-inline-alert__description > :where(ul) {
      padding-left: 1.25em;
    }

    :host ::ng-deep .ds-inline-alert__description :where(a) {
      color: inherit;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 0.16em;
    }

    .ds-inline-alert--size-m {
      --_action-content-min-width: var(--ds-inline-alert-action-content-min-width);
      --_border-radius: var(--ds-inline-alert-border-radius);
      --_icon-size: var(--ds-inline-alert-icon-size);
      --_padding-x: var(--ds-inline-alert-padding-x);
      --_padding-y: var(--ds-inline-alert-padding-y);
    }

    .ds-inline-alert--size-s {
      --_action-content-min-width: var(--ds-inline-alert-small-action-content-min-width);
      --_border-radius: var(--ds-inline-alert-small-border-radius);
      --_icon-size: var(--ds-inline-alert-small-icon-size);
      --_padding-x: var(--ds-inline-alert-small-padding-x);
      --_padding-y: var(--ds-inline-alert-small-padding-y);
    }

    .ds-inline-alert--emphasis-filled {
      --_background: var(--_filled-background);
      --_border-color: transparent;
      --_border-width: 0px;
    }

    .ds-inline-alert--emphasis-outline {
      --_background: var(--ds-inline-alert-outline-background);
      --_border-color: var(--_semantic-color);
      --_border-width: var(--ds-inline-alert-border-width);
    }

    .ds-inline-alert--neutral {
      --_filled-background: var(--ds-inline-alert-neutral-filled-background);
      --_semantic-color: var(--ds-inline-alert-neutral-semantic-color);
    }

    .ds-inline-alert--error {
      --_filled-background: var(--ds-inline-alert-error-filled-background);
      --_semantic-color: var(--ds-inline-alert-error-semantic-color);
    }

    .ds-inline-alert--warning {
      --_filled-background: var(--ds-inline-alert-warning-filled-background);
      --_semantic-color: var(--ds-inline-alert-warning-semantic-color);
    }

    .ds-inline-alert--success {
      --_filled-background: var(--ds-inline-alert-success-filled-background);
      --_semantic-color: var(--ds-inline-alert-success-semantic-color);
    }

    .ds-inline-alert--info {
      --_filled-background: var(--ds-inline-alert-info-filled-background);
      --_semantic-color: var(--ds-inline-alert-info-semantic-color);
    }
  `],
})
export class DsInlineAlertComponent {
  @Input() type: DsInlineAlertType = 'neutral';
  @Input() icon?: DsIconName | string;
  @Input() title = '';
  @Input() size: DsInlineAlertSize = 'm';
  @Input() emphasis: DsInlineAlertEmphasis = 'filled';
  @Input() shadow: DsInlineAlertShadow = 'none';
  @Input() actionLayout: DsInlineAlertActionLayout = 'stacked';
  @Input({ transform: booleanAttribute }) showCloseButton = false;
  @Input() closeAriaLabel = 'Cerrar alerta';
  @Input() description =
    'Lorem ipsum dolor sit, amet consectetur adipisicing elit. Voluptatem, dolores nam? Tempora quaerat vitae.';

  @Output() dismissed = new EventEmitter<void>();

  get hasTitle(): boolean {
    return this.title.trim().length > 0;
  }

  get hostClasses(): string {
    return `ds-inline-alert--${this.type} ds-inline-alert--size-${this.size} ds-inline-alert--emphasis-${this.emphasis} ds-inline-alert--shadow-${this.shadow} ds-inline-alert--action-${this.actionLayout}`;
  }

  get closeButtonSize(): 'xs' | 's' {
    return this.size === 's' ? 'xs' : 's';
  }

  get iconName(): string {
    if (this.icon) {
      return this.icon;
    }

    switch (this.type) {
      case 'warning':
        return 'warning';
      case 'success':
        return 'check_circle';
      case 'info':
        return 'info';
      case 'neutral':
      case 'error':
      default:
        return 'error';
    }
  }
}
